/**
 * Cycle Participant Groups Store
 *
 * First-class group participation: "this group participates in this cycle."
 * Persisted in cycle_participant_groups (new table). Effective participants
 * are resolved at runtime via effectiveParticipantResolver (direct users +
 * members of these groups, with duplicate handling).
 *
 * SCHEMA REQUIRED (create table):
 *   cycle_participant_groups (
 *     id uuid primary key,
 *     cycle_id uuid not null references cycles(id),
 *     group_id uuid not null references groups(id),
 *     role text not null default 'contributor',
 *     status text not null default 'active',
 *     added_by uuid not null,
 *     added_at timestamptz not null,
 *     removed_at timestamptz,
 *     removed_by uuid,
 *     unique(cycle_id, group_id)
 *   )
 */

import { create } from 'zustand';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import type { ParticipantRole } from './cycleParticipantsStore';

// ============================================================================
// Types
// ============================================================================

export type CycleParticipantGroupStatus = 'active' | 'removed';

export interface CycleParticipantGroup {
  id: string;
  cycleId: string;
  groupId: string;
  role: ParticipantRole;
  status: CycleParticipantGroupStatus;
  addedBy: string;
  addedAt: Date;
  removedAt?: Date;
  removedBy?: string;
}

interface CycleParticipantGroupsState {
  participantGroups: CycleParticipantGroup[];
  isLoading: boolean;
  error: string | null;

  fetchParticipantGroupsForCycle: (cycleId: string) => Promise<void>;
  addParticipantGroup: (input: {
    cycleId: string;
    groupId: string;
    role: ParticipantRole;
    addedBy: string;
  }) => Promise<CycleParticipantGroup | null>;
  removeParticipantGroup: (id: string, removedBy: string) => Promise<boolean>;

  getParticipantGroupsForCycle: (cycleId: string) => CycleParticipantGroup[];
  getActiveParticipantGroupsForCycle: (cycleId: string) => CycleParticipantGroup[];
}

const TABLE_NAME = 'cycle_participant_groups';

function rowToParticipantGroup(row: Record<string, unknown>): CycleParticipantGroup {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    groupId: row.group_id as string,
    role: row.role as ParticipantRole,
    status: (row.status as CycleParticipantGroupStatus) || 'active',
    addedBy: row.added_by as string,
    addedAt: row.added_at != null ? new Date(row.added_at as string) : new Date(0),
    removedAt: row.removed_at ? new Date(row.removed_at as string) : undefined,
    removedBy: row.removed_by as string | undefined,
  };
}

function participantGroupToRow(p: Partial<CycleParticipantGroup>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (p.id != null) out.id = p.id;
  if (p.cycleId != null) out.cycle_id = p.cycleId;
  if (p.groupId != null) out.group_id = p.groupId;
  if (p.role != null) out.role = p.role;
  if (p.status != null) out.status = p.status;
  if (p.addedBy != null) out.added_by = p.addedBy;
  if (p.addedAt != null) out.added_at = p.addedAt.toISOString();
  if (p.removedAt !== undefined) out.removed_at = p.removedAt?.toISOString() ?? null;
  if (p.removedBy !== undefined) out.removed_by = p.removedBy ?? null;
  return out;
}

// ============================================================================
// Store
// ============================================================================

export const useCycleParticipantGroupsStore = create<CycleParticipantGroupsState>()((set, get) => ({
  participantGroups: [],
  isLoading: false,
  error: null,

  fetchParticipantGroupsForCycle: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('cycle_id', cycleId)
        .order('added_at', { ascending: true });
      if (error) {
        // Table may not exist yet; treat as empty so UI still works
        if (error.code === '42P01' || error.message?.toLowerCase().includes('does not exist')) {
          set(state => {
            const other = state.participantGroups.filter(g => g.cycleId !== cycleId);
            return { participantGroups: other, isLoading: false, error: null };
          });
          return;
        }
        set({ isLoading: false, error: error.message });
        return;
      }
      const list = (data || []).map(rowToParticipantGroup);
      set(state => {
        const other = state.participantGroups.filter(g => g.cycleId !== cycleId);
        return { participantGroups: [...other, ...list], isLoading: false, error: null };
      });
    } catch (err) {
      set({ isLoading: false, error: String(err) });
    }
  },

  addParticipantGroup: async (input) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return null;
    try {
      const existing = await supabase
        .from(TABLE_NAME)
        .select('id, status')
        .eq('cycle_id', input.cycleId)
        .eq('group_id', input.groupId)
        .maybeSingle();
      if (existing.data) {
        if (existing.data.status === 'removed') {
          const { data: updated, error: upErr } = await supabase
            .from(TABLE_NAME)
            .update({ status: 'active', removed_at: null, removed_by: null })
            .eq('id', existing.data.id)
            .select()
            .single();
          if (upErr) {
            set({ error: upErr.message });
            return null;
          }
          const g = rowToParticipantGroup(updated);
          set(state => ({
            participantGroups: state.participantGroups.map(p => (p.id === g.id ? g : p)),
            error: null,
          }));
          return g;
        }
        return rowToParticipantGroup(existing.data as unknown as Record<string, unknown>);
      }
      const newRow: CycleParticipantGroup = {
        id: generateUUID(),
        cycleId: input.cycleId,
        groupId: input.groupId,
        role: input.role,
        status: 'active',
        addedBy: input.addedBy,
        addedAt: new Date(),
      };
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert(participantGroupToRow(newRow))
        .select()
        .single();
      if (error) {
        set({ error: error.message });
        return null;
      }
      const inserted = rowToParticipantGroup(data as unknown as Record<string, unknown>);
      set(state => ({
        participantGroups: [...state.participantGroups, inserted],
        error: null,
      }));
      return inserted;
    } catch (err) {
      set({ error: String(err) });
      return null;
    }
  },

  removeParticipantGroup: async (id, removedBy) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;
    const g = get().participantGroups.find(p => p.id === id);
    if (!g) return false;
    const now = new Date();
    try {
      const { error } = await supabase
        .from(TABLE_NAME)
        .update({ status: 'removed', removed_at: now.toISOString(), removed_by: removedBy })
        .eq('id', id);
      if (error) {
        set({ error: error.message });
        return false;
      }
      set(state => ({
        participantGroups: state.participantGroups.map(p =>
          p.id === id ? { ...p, status: 'removed' as CycleParticipantGroupStatus, removedAt: now, removedBy } : p
        ),
        error: null,
      }));
      return true;
    } catch (err) {
      set({ error: String(err) });
      return false;
    }
  },

  getParticipantGroupsForCycle: (cycleId) =>
    get().participantGroups.filter(g => g.cycleId === cycleId),

  getActiveParticipantGroupsForCycle: (cycleId) =>
    get().participantGroups.filter(g => g.cycleId === cycleId && g.status === 'active'),
}));
